<template lang="pug">
.container.grid-xl
  fieldset(:disabled="loading")
    form(@submit.prevent="onSubmit")
      page-title(icon="fas fa-book" title="Editar livros")
        div(slot="actions")
          button.btn.btn-primary(:disabled="disabled") Salvar
      .flex-centered.full-height(v-if="error") #[error-box(:error="error")]
      div(v-else)
        titles(v-model="product.productInfos")
        .columns
          .column.col-6.col-sm-12
            form-group(label="Título" :validation="$v.product" field="title")
              input.form-input(v-model.trim="$v.product.title.$model")
          .column.col-6.col-sm-12
            form-group(label="Preço")
              masked-money.form-input(v-model="product.price" v-bind="valueAttrs")
        .divider
        .columns
          .column.col-6.col-sm-12
            form-group(label="Data de publicação")
              datepicker(v-model="product.publishDate")
          .column.col-6.col-sm-12
            form-group(label="Código")
              input.form-input(v-model="product.internalCode")
          .column.col-12
            form-group(label="Resumo")
              textarea.form-input(v-model="product.brief" rows="3")
          .column.col-12
            form-group(label="Palavras-chave")
              keywords-autocomplete(v-model="product.keywords")
        .divider
        .columns
          .column.col-6.col-sm-8
            form-group(label="Capa")
              file-upload(
                v-model="product.cover"
                @upload-start="uploading = true"
                @upload-end="uploading = false"
              )
        .divider
        .columns
          .column.col-6.col-sm-12
            form-switch(v-model="product.customerCourtesy" label="Cortesia para clientes")
          .column.col-6.col-sm-12
            form-switch(v-model="product.subscriberCourtesy" label="Cortesia para assinantes")
          .column.col-12
            form-switch(v-model="product.digitalProduct" label="Produto digital")
          .column.col-12.divider
          .column.col-12
            form-switch(v-model="product.monetizationForFiliations" label="As compras deste produto entrarão no percentual destinado aos produtores de conteúdo")
        .divider
        label.form-label Quais idiomas esse livro possui
        .form-group
          label.form-switch(v-for="(lang, value) in languages")
            input(type="checkbox" v-model="product.availableLanguages" :value="value")
            i.form-icon
            | {{ lang }}
            .float-right(v-if="help")
              .popover.popover-left
                a.c-hand.text-bold ?
                .popover-container
                  .card
                    .card-body {{ help }}
        .divider
        fill-loading(v-if="loading")
        product-items-list(v-if="!initialLoading" :productItems="productItems" :productType="product.productType" :disabled="loading"
                           @add-item="addItemToList" @edit-item="updateItemList" @remove-item="deleteItem")

</template>

<script>
import ProductModel from '@/lib/products/ProductModel'
import Titles from './components/Titles.vue'
import { required, minLength } from 'vuelidate/lib/validators'
import ProductItemsList from './components/ProductItemsList'
import productsServices from '@/services/products'
import productItemsServices from '@/services/productItems'
import { deepClone } from 'paliari-js-utils'
import { mapState } from 'vuex'
import languages from '@/lib/products/languages'

export default {
  data() {
    return {
      loading: false,
      product: ProductModel('book'),
      productItems: [],
      uploading: false,
      error: null,
      initialLoading: false,
    }
  },

  components: { ProductItemsList, Titles },

  computed: {
    ...mapState('systemParameters', ['systemParameters']),

    languages() {
      return languages
    },

    valueAttrs() {
      return { prefix: 'R$ ' }
    },

    disabled() {
      const { uploading, loading, $v, initialLoading } = this
      return uploading || loading || $v.$invalid || initialLoading
    },
  },

  async created() {
    try {
      this.initialLoading = true

      const product = await productsServices.show(this.$route.params.id)
      this.product = product.data

      const productItems = await productItemsServices.list(this.product.id)
      this.productItems = productItems.data
    } catch (e) {
      this.error = e
    } finally {
      this.initialLoading = false
    }
  },

  methods: {
    async addItemToList(productItem) {
      try {
        this.loading = true
        const { data } = await productItemsServices.create(
          productItem,
          this.product,
          this.systemParameters,
        )
        this.productItems.push(data)
      } catch (e) {
        this.$toaster.error(`Erro ao criar o item ${productItem.title}`)
      } finally {
        this.loading = false
      }
    },

    async updateItemList(productItem) {
      try {
        this.loading = true
        const { data } = await productItemsServices.update(
          productItem,
          this.product,
          this.systemParameters,
        )
        const itemIndex = this.productItems.findIndex(
          i => i.id === productItem.id,
        )
        this.$set(this.productItems, itemIndex, data)
      } catch (e) {
        this.$toaster.error(`Erro ao atualizar o item ${productItem.title}`)
      } finally {
        this.loading = false
      }
    },

    async deleteItem(productItem) {
      try {
        this.loading = true
        await productItemsServices.destroy(this.product.id, productItem.id)

        const itemIndex = this.productItems.findIndex(
          i => i.id === productItem.id,
        )
        this.productItems.splice(itemIndex, 1)
      } catch (e) {
        this.$toaster.error(`Erro ao excluir item ${productItem.title}`)
      } finally {
        this.loading = false
      }
    },

    async onSubmit() {
      try {
        this.loading = true
        const productObject = deepClone(this.product)
        const { data } = await productsServices.update(productObject)
        this.$toaster.success('Livro alterado com sucesso')
        this.product = data
      } catch (e) {
        this.$toaster.error('Erro ao salvar alterações do produto')
      } finally {
        this.loading = false
      }
    },
  },

  validations: {
    product: { title: { required } },
    productItems: { required, minLength: minLength(1) },
  },
}
</script>

<style lang="stylus" scoped>
textarea
  width 100%
</style>
